import { useAuth, authorizedFetch, API_ORIGIN } from '@yeshuman/sdk'
import type { TalentProfilePreview } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { INTERVIEWER_CANDIDATES_QUERY_KEY } from './use-interviewer-candidates'
import type { BriefBoardColumn } from '../lib/brief-board'
import type { ScorecardSummary } from './use-interview-kits'

export {
  BRIEF_BOARD_COLUMNS,
  briefCandidateBoardColumn,
  canMoveBriefCandidate,
  type BriefBoardColumn,
} from '../lib/brief-board'

export interface BriefSkill {
  id: string
  skill_name: string
  requirement_type: 'required' | 'preferred'
}


export type InterviewOutcomeState = 'scheduled' | 'passed' | 'failed' | 'hired' | 'not_progressed'

export const INTERVIEW_OUTCOME_NOTES_MAX = 500

/** Current recruiter-recorded outcome for a candidate on a job (TAL-6). */
export interface InterviewOutcome {
  id: string
  profile_id: string
  opportunity_id: string
  opportunity_title: string | null
  brief_id: string | null
  brief_title: string | null
  state: InterviewOutcomeState
  state_label: string
  notes: string
  updated_at: string
  updated_by_name: string | null
}

export interface SetInterviewOutcomeData {
  state: InterviewOutcomeState
  notes?: string
}

export interface BriefCandidate {
  id: string
  profile_id: string
  candidate_name: string
  notes: string
  saved_at: string
  profile_preview?: TalentProfilePreview | null
  evaluation_id?: string | null
  match_score?: number | null
  analysis_headline?: string | null
  /** True when this candidate already has an application in the interview stage for the brief's job. */
  in_interview?: boolean
  application_id?: string | null
  outcome?: InterviewOutcome | null
  stage?: 'identified' | 'shortlisted'
  /** Where the candidate sits on the brief board; Interview onwards is derived server-side. */
  board_column?: BriefBoardColumn
  /** Newest interviewer scorecard for this candidate on the brief's job. */
  scorecard?: ScorecardSummary | null
}

export interface SendToInterviewResponse {
  application_id: string
  opportunity_id: string
  profile_id: string
  stage: string
  created: boolean
}

export interface HiredSkillSuggestion {
  id: string
  profile_id: string
  candidate_name: string
  opportunity_id: string
  skill_names: string[]
  status: 'pending' | 'accepted' | 'dismissed'
}

export interface Brief {
  id: string
  title: string
  description: string
  location: string
  instructions?: string
  status: 'draft' | 'active' | 'closed'
  priority: 'high' | 'medium' | 'low'
  opportunity_id: string | null
  opportunity_title: string | null
  client_organisation_id: string | null
  client_organisation_name: string | null
  role_id?: string | null
  role_title?: string | null
  role?: { id: string; title: string; seniority: string } | null
  skills: BriefSkill[]
  /** Pending hired-signal suggestions. Absent on older payloads. */
  hired_suggestions?: HiredSkillSuggestion[]
  saved_candidates_count: number
  created_at: string
  updated_at: string
}

export interface CreateBriefData {
  title: string
  description?: string
  location?: string
  instructions?: string
  opportunity_id?: string
  client_organisation_id?: string
  role_id?: string
  status?: 'draft' | 'active' | 'closed'
  priority?: 'high' | 'medium' | 'low'
  required_skills?: string[]
  preferred_skills?: string[]
}

export interface UpdateBriefData {
  title?: string
  description?: string
  location?: string
  instructions?: string
  status?: 'draft' | 'active' | 'closed'
  priority?: 'high' | 'medium' | 'low'
  /** null unlinks the role; the skills and rubric it seeded stay. */
  role_id?: string | null
  required_skills?: string[]
  preferred_skills?: string[]
}

export interface SearchCandidatesData {
  required_skills?: string[]
  preferred_skills?: string[]
  location?: string
  experience_level?: 'junior' | 'mid' | 'senior' | 'executive'
  min_experience_years?: number
  max_experience_years?: number
  instructions?: string
  llm_similarity_threshold?: number
  limit?: number
}

export interface CandidateMatch {
  rank: number
  profile_id: string
  candidate_name: string
  final_score: number
  structured_score: number
  semantic_score: number
  llm_score?: number
  llm_reasoning?: string
  was_llm_judged: boolean
  found_by?: string[]
  category?: "top" | "compelling" | "precluded"
  new_since_last_run?: boolean
  evaluation_id?: string | null
  analysis_headline?: string | null
  avatar_url?: string | null
}

export interface SearchCandidatesResponse {
  session_id?: string
  evaluation_run_id?: string | null
  previous_run_at?: string | null
  total_candidates_evaluated: number
  llm_judged_count: number
  top_matches: CandidateMatch[]
  top_candidates?: CandidateMatch[]
  compelling_candidates?: CandidateMatch[]
  precluded_candidates?: CandidateMatch[]
  strategies_used?: string[]
  search_summary?: string
}

export interface SaveCandidateData {
  profile_id?: string
  profile_ids?: string[]
  notes?: string
}

export interface SaveCandidateResponse {
  brief_id: string
  brief_title: string
  saved_count: number
  skipped_count: number
  total_requested: number
}

export function useBriefs(status?: string) {
  const { token } = useAuth()

  const briefsQuery = useQuery({
    queryKey: ['briefs', status],
    queryFn: async (): Promise<Brief[]> => {
      if (!token) throw new Error('Not authenticated')

      const params = new URLSearchParams()
      if (status) params.append('status', status)

      const url = `${API_ORIGIN}/api/briefs/${params.toString() ? '?' + params.toString() : ''}`

      const response = await authorizedFetch(url)

      if (!response.ok) {
        throw new Error('Failed to fetch briefs')
      }

      return response.json()
    },
    enabled: !!token,
    staleTime: 30 * 1000, // 30 seconds
  })

  return {
    briefs: briefsQuery.data || [],
    isLoading: briefsQuery.isLoading,
    error: briefsQuery.error,
    refetch: briefsQuery.refetch,
  }
}

export function useBrief(briefId: string | null) {
  const { token } = useAuth()

  const briefQuery = useQuery({
    queryKey: ['brief', briefId],
    queryFn: async (): Promise<Brief> => {
      if (!token) throw new Error('Not authenticated')
      if (!briefId) throw new Error('Brief ID required')

      const response = await authorizedFetch(`${API_ORIGIN}/api/briefs/${briefId}/`)

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Brief not found')
        }
        throw new Error('Failed to fetch brief')
      }

      const data = await response.json()
      // Django Ninja returns tuple format (status_code, data) for non-200 responses
      // For 200 responses, it returns just the data
      if (Array.isArray(data) && data.length === 2 && typeof data[0] === 'number') {
        return data[1]
      }
      return data
    },
    enabled: !!token && !!briefId,
    staleTime: 30 * 1000,
  })

  return {
    brief: briefQuery.data,
    isLoading: briefQuery.isLoading,
    error: briefQuery.error,
    refetch: briefQuery.refetch,
  }
}

export function useCreateBrief() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const createBriefMutation = useMutation({
    mutationFn: async (data: CreateBriefData): Promise<Brief> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`${API_ORIGIN}/api/briefs/`, {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create brief')
      }

      const result = await response.json()
      return result[1] || result // Handle tuple response format
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['briefs'] })
    },
  })

  return {
    createBrief: createBriefMutation.mutate,
    isCreating: createBriefMutation.isPending,
    createError: createBriefMutation.error,
  }
}

export function useUpdateBrief() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const updateBriefMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBriefData }): Promise<Brief> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`${API_ORIGIN}/api/briefs/${id}/`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update brief')
      }

      const result = await response.json()
      return result[1] || result // Handle tuple response format
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['briefs'] })
      queryClient.invalidateQueries({ queryKey: ['brief', variables.id] })
    },
  })

  return {
    updateBrief: updateBriefMutation.mutate,
    isUpdating: updateBriefMutation.isPending,
    updateError: updateBriefMutation.error,
  }
}

export function useDeleteBrief() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const deleteBriefMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`${API_ORIGIN}/api/briefs/${id}/`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete brief')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['briefs'] })
    },
  })

  return {
    deleteBrief: deleteBriefMutation.mutate,
    isDeleting: deleteBriefMutation.isPending,
    deleteError: deleteBriefMutation.error,
  }
}

export function useSearchCandidates() {
  const { token } = useAuth()

  const searchCandidatesMutation = useMutation({
    mutationFn: async ({ briefId, data }: { briefId: string; data: SearchCandidatesData }): Promise<SearchCandidatesResponse> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`${API_ORIGIN}/api/briefs/${briefId}/search-candidates/`, {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to search candidates')
      }

      const result = await response.json()
      return result[1] || result // Handle tuple response format
    },
  })

  return {
    searchCandidates: searchCandidatesMutation.mutate,
    isSearching: searchCandidatesMutation.isPending,
    searchError: searchCandidatesMutation.error,
    searchData: searchCandidatesMutation.data,
  }
}

export function useSaveCandidate() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const saveCandidateMutation = useMutation({
    mutationFn: async ({ briefId, data }: { briefId: string; data: SaveCandidateData }): Promise<SaveCandidateResponse> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`${API_ORIGIN}/api/briefs/${briefId}/save-candidate/`, {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to save candidate')
      }

      const result = await response.json()
      return result[1] || result // Handle tuple response format
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['brief', variables.briefId] })
      queryClient.invalidateQueries({ queryKey: ['brief-candidates', variables.briefId] })
      queryClient.invalidateQueries({ queryKey: ['briefs'] })
    },
  })

  return {
    saveCandidate: saveCandidateMutation.mutate,
    isSaving: saveCandidateMutation.isPending,
    saveError: saveCandidateMutation.error,
  }
}

export function useSendCandidateToInterview() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      briefId,
      profileId,
    }: {
      briefId: string
      profileId: string
    }): Promise<SendToInterviewResponse> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(
        `${API_ORIGIN}/api/briefs/${briefId}/candidates/${profileId}/send-to-interview/`,
        { method: 'POST' },
      )

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to send candidate to interview')
      }

      const result = await response.json()
      return result[1] || result // Handle tuple response format
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['brief-candidates', variables.briefId] })
      queryClient.invalidateQueries({ queryKey: ['brief', variables.briefId] })
      queryClient.invalidateQueries({ queryKey: ['briefs'] })
      queryClient.invalidateQueries({ queryKey: INTERVIEWER_CANDIDATES_QUERY_KEY })
    },
  })

  return {
    sendToInterview: mutation.mutate,
    sendToInterviewAsync: mutation.mutateAsync,
    isSending: mutation.isPending,
    sendError: mutation.error,
  }
}

export function useBriefCandidates(briefId: string | null) {
  const { token } = useAuth()

  const candidatesQuery = useQuery({
    queryKey: ['brief-candidates', briefId],
    queryFn: async (): Promise<BriefCandidate[]> => {
      if (!token) throw new Error('Not authenticated')
      if (!briefId) throw new Error('Brief ID required')

      const response = await authorizedFetch(`${API_ORIGIN}/api/briefs/${briefId}/candidates/`)

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Brief not found')
        }
        throw new Error('Failed to fetch candidates')
      }

      const data = await response.json()
      // Django Ninja returns tuple format (status_code, data) for non-200 responses
      // For 200 responses, it returns just the data
      if (Array.isArray(data) && data.length === 2 && typeof data[0] === 'number') {
        return data[1]
      }
      return data
    },
    enabled: !!token && !!briefId,
    staleTime: 30 * 1000,
  })

  return {
    candidates: candidatesQuery.data || [],
    isLoading: candidatesQuery.isLoading,
    error: candidatesQuery.error,
    refetch: candidatesQuery.refetch,
  }
}

export function useSetCandidateOutcome() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      briefId,
      profileId,
      data,
    }: {
      briefId: string
      profileId: string
      data: SetInterviewOutcomeData
    }): Promise<InterviewOutcome> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(
        `${API_ORIGIN}/api/briefs/${briefId}/candidates/${profileId}/outcome/`,
        { method: 'POST', body: JSON.stringify(data) },
      )

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to record outcome')
      }

      const result = await response.json()
      return result[1] || result // Handle tuple response format
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['brief-candidates', variables.briefId] })
      queryClient.invalidateQueries({ queryKey: ['brief', variables.briefId] })
      queryClient.invalidateQueries({ queryKey: ['profile-interview-outcomes', variables.profileId] })
    },
  })

  return {
    setOutcome: mutation.mutate,
    isSettingOutcome: mutation.isPending,
    setOutcomeError: mutation.error,
  }
}

export function useMoveBriefCandidate() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      briefId,
      profileId,
      column,
      notes,
    }: {
      briefId: string
      profileId: string
      column: BriefBoardColumn
      notes?: string
    }): Promise<{ profile_id: string; board_column: BriefBoardColumn }> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(
        `${API_ORIGIN}/api/briefs/${briefId}/candidates/${profileId}/stage/`,
        { method: 'PATCH', body: JSON.stringify({ column, notes }) },
      )

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to move candidate')
      }

      return response.json()
    },
    onMutate: async ({ briefId, profileId, column }) => {
      const key = ['brief-candidates', briefId]
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<BriefCandidate[]>(key)
      queryClient.setQueryData<BriefCandidate[]>(key, (rows) =>
        rows?.map((row) => (row.profile_id === profileId ? { ...row, board_column: column } : row)),
      )
      return { previous }
    },
    onError: (_error, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['brief-candidates', variables.briefId], context.previous)
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['brief-candidates', variables.briefId] })
      queryClient.invalidateQueries({ queryKey: ['brief', variables.briefId] })
      queryClient.invalidateQueries({ queryKey: ['briefs'] })
      queryClient.invalidateQueries({ queryKey: INTERVIEWER_CANDIDATES_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['profile-interview-outcomes', variables.profileId] })
    },
  })

  return {
    moveCandidate: mutation.mutate,
    isMoving: mutation.isPending,
  }
}

export function useHiredSuggestionActions(briefId: string) {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const respond = async (suggestionId: string, action: 'accept' | 'dismiss'): Promise<HiredSkillSuggestion> => {
    if (!token) throw new Error('Not authenticated')

    const response = await authorizedFetch(
      `${API_ORIGIN}/api/briefs/${briefId}/suggestions/${suggestionId}/${action}/`,
      { method: 'POST' },
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to ${action} suggestion`)
    }

    const result = await response.json()
    return result[1] || result
  }

  const acceptMutation = useMutation({
    mutationFn: (suggestionId: string) => respond(suggestionId, 'accept'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brief', briefId] })
      queryClient.invalidateQueries({ queryKey: ['briefs'] })
    },
  })

  const dismissMutation = useMutation({
    mutationFn: (suggestionId: string) => respond(suggestionId, 'dismiss'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brief', briefId] })
    },
  })

  const busyId = acceptMutation.isPending
    ? acceptMutation.variables
    : dismissMutation.isPending
      ? dismissMutation.variables
      : null
  const busyAction: 'accept' | 'dismiss' | null = acceptMutation.isPending
    ? 'accept'
    : dismissMutation.isPending
      ? 'dismiss'
      : null

  return {
    acceptSuggestion: acceptMutation.mutate,
    dismissSuggestion: dismissMutation.mutate,
    busyId,
    busyAction,
  }
}

/** Outcomes recorded for a profile on jobs linked to briefs the signed-in recruiter owns. */
export function useProfileInterviewOutcomes(profileId: string | null | undefined) {
  const { token } = useAuth()

  const outcomesQuery = useQuery({
    queryKey: ['profile-interview-outcomes', profileId],
    queryFn: async (): Promise<InterviewOutcome[]> => {
      if (!token) throw new Error('Not authenticated')
      if (!profileId) throw new Error('Profile ID required')

      const response = await authorizedFetch(`${API_ORIGIN}/api/briefs/profiles/${profileId}/outcomes/`)

      if (!response.ok) {
        throw new Error('Failed to fetch interview outcomes')
      }

      const data = await response.json()
      if (Array.isArray(data) && data.length === 2 && typeof data[0] === 'number') {
        return data[1]
      }
      return data
    },
    enabled: !!token && !!profileId,
    staleTime: 30 * 1000,
  })

  return {
    outcomes: outcomesQuery.data || [],
    isLoading: outcomesQuery.isLoading,
    error: outcomesQuery.error,
    refetch: outcomesQuery.refetch,
  }
}

